<template>
  <BaseModal
    :open="modelValue"
    :title="t('auth.sponsor_popup_title')"
    :z-index="10001"
    size="sm"
    @close="close"
  >
    <div class="sponsor-popup">
      <h3 class="sponsor-popup__title">{{ t('auth.sponsor_popup_title') }}</h3>
      <p class="sponsor-popup__subtitle">{{ t('auth.sponsor_popup_subtitle') }}</p>

      <div class="sponsor-popup__field">
        <input
          ref="inputRef"
          v-model="sponsorLogin"
          type="text"
          class="sponsor-popup__input"
          :class="{ 'sponsor-popup__input--error': sponsorError }"
          :placeholder="t('auth.enterInviterLogin')"
          :disabled="isLoading"
          @keydown.enter.prevent="handleContinue"
        />
        <span v-if="sponsorError" class="sponsor-popup__error">{{ sponsorError }}</span>
      </div>

      <button
        class="sponsor-popup__btn sponsor-popup__btn--primary"
        :disabled="!sponsorLogin.trim() || isLoading"
        @click="handleContinue"
      >
        {{ isLoading ? t('auth.loading') : t('auth.sponsor_popup_continue') }}
      </button>

      <button
        class="sponsor-popup__btn sponsor-popup__btn--ghost"
        :disabled="isLoading"
        @click="handleNoSponsor"
      >
        {{ t('auth.sponsor_popup_no_inviter') }}
      </button>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useProfileStore } from '@/modules/profile/store/profileStore'
import BaseModal from '@/shared/components/base/BaseModal.vue'

const props = defineProps<{
  modelValue: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'confirmed', login: string | null, referralId: number | null): void
}>()

const { t } = useI18n()
const profileStore = useProfileStore()

const sponsorLogin = ref('')
const sponsorError = ref('')
const isLoading = ref(false)
const inputRef = ref<HTMLInputElement | null>(null)

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      sponsorLogin.value = ''
      sponsorError.value = ''
      isLoading.value = false
      nextTick(() => inputRef.value?.focus())
    }
  },
)

const close = () => {
  if (isLoading.value) return
  emit('update:modelValue', false)
}

const handleContinue = async () => {
  const login = sponsorLogin.value.trim()
  if (!login) return

  sponsorError.value = ''
  isLoading.value = true

  try {
    const res = (await profileStore.searchSponsor({ login })) as any
    if (res.result !== 'success') {
      sponsorError.value = t('auth.inviterNotFound')
      return
    }
    localStorage.setItem('partner_referral_code', login)
    emit('confirmed', login, res.rid ?? null)
    emit('update:modelValue', false)
  } catch {
    sponsorError.value = t('auth.inviterNotFound')
  } finally {
    isLoading.value = false
  }
}

const handleNoSponsor = () => {
  emit('confirmed', null, null)
  emit('update:modelValue', false)
}
</script>

<style scoped lang="scss">
.sponsor-popup {
  text-align: center;
}

.sponsor-popup__title {
  font-size: var(--ds-text-size-h-sm);
  font-weight: var(--ds-weight-bold);
  color: var(--ds-text);
  margin: 0 0 8px;
}

.sponsor-popup__subtitle {
  font-size: var(--ds-text-size-body);
  color: var(--ds-text-secondary);
  margin: 0 0 24px;
  line-height: 1.5;
}

.sponsor-popup__field {
  margin-bottom: 20px;
  text-align: left;
}

.sponsor-popup__input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-radius-card);
  font-size: var(--p-text-15);
  transition: border-color 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--ds-accent);
  }

  &--error {
    border-color: var(--ds-danger) !important;
  }
}

.sponsor-popup__error {
  display: block;
  color: var(--ds-danger);
  font-size: var(--ds-text-size-caption);
  margin-top: 6px;
}

.sponsor-popup__btn {
  width: 100%;
  padding: 13px 24px;
  border-radius: var(--p-radius-50);
  font-size: var(--p-text-15);
  font-weight: var(--ds-weight-semibold);
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;

  &--primary {
    background: var(--ds-accent);
    color: var(--ds-surface);
    margin-bottom: 10px;

    &:hover:not(:disabled) {
      background: var(--ds-accent);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &--ghost {
    background: transparent;
    color: var(--ds-text-muted);

    &:hover:not(:disabled) {
      color: var(--ds-text-secondary);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>